import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { 
  Database, 
  Settings, 
  TestTube, 
  Save, 
  RefreshCw, 
  Clock, 
  Play, 
  Pause, 
  Trash2, 
  Plus,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { tituloService } from '@/services/tituloService';
import { ExternalApiFullConfig, ExternalApiJob, ConnectionTestResult } from '@/types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export const ExternalApiConfigTab: React.FC = () => {
  // Função para calcular período padrão (01/06/2025 até hoje + 60 dias)
  const getDefaultPeriod = () => {
    const hoje = new Date();
    const dataFim = new Date();
    dataFim.setDate(hoje.getDate() + 60); // Hoje + 60 dias

    // Data inicial fixa: 01/06/2025
    const dataInicio = new Date('2025-06-01');

    return {
      dataInicio: dataInicio.toISOString().split('T')[0]!,
      dataFim: dataFim.toISOString().split('T')[0]!,
    };
  };

  // Estados da configuração
  const defaultPeriod = getDefaultPeriod();
  const [config, setConfig] = useState<ExternalApiFullConfig>({
    enabled: false,
    url: '',
    token: '',
    cnpj: '',
    timeout: 30000,
    defaultPeriodDays: 30,
    dataInicio: defaultPeriod.dataInicio,
    dataFim: defaultPeriod.dataFim,
    autoSyncEnabled: false,
    syncInterval: '0 8 * * *',
  });
  
  // Estados dos jobs
  const [jobs, setJobs] = useState<ExternalApiJob[]>([]);
  
  // Estados de UI
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showJobModal, setShowJobModal] = useState(false);
  const [editingJob, setEditingJob] = useState<ExternalApiJob | null>(null);
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);
  const [showTestResult, setShowTestResult] = useState(false);

  // Carregar dados iniciais
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [configData, jobsData] = await Promise.all([
        tituloService.getExternalApiFullConfig(),
        tituloService.getExternalApiJobs(),
      ]);
      
      // Sempre garantir que as datas estejam preenchidas com valores padrão
      const defaultPeriod = getDefaultPeriod();
      setConfig({
        ...configData,
        dataInicio: configData.dataInicio || defaultPeriod.dataInicio,
        dataFim: configData.dataFim || defaultPeriod.dataFim,
      });
      setJobs(jobsData);
    } catch (error) {
      toast.error('Erro ao carregar configurações');
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      const updatedConfig = await tituloService.updateExternalApiConfig(config);
      setConfig(updatedConfig);
      toast.success('Configuração salva com sucesso!');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao salvar configuração');
      console.error('Erro ao salvar configuração:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      const result = await tituloService.testExternalApiConnection(config);
      setTestResult(result);
      setShowTestResult(true);
      
      if (result.success) {
        toast.success('Conexão testada com sucesso!');
      } else {
        toast.error('Falha no teste de conexão');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao testar conexão');
      console.error('Erro ao testar conexão:', error);
    } finally {
      setTesting(false);
    }
  };

  const handleCreateJob = async (jobData: {
    name: string;
    description: string;
    cronExpression: string;
    enabled?: boolean;
  }) => {
    try {
      const newJob = await tituloService.createExternalApiJob(jobData);
      setJobs(prev => [newJob, ...prev]);
      toast.success('Job criado com sucesso!');
      setShowJobModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao criar job');
      console.error('Erro ao criar job:', error);
    }
  };

  const handleUpdateJob = async (jobId: string, updates: any) => {
    try {
      const updatedJob = await tituloService.updateExternalApiJob(jobId, updates);
      setJobs(prev => prev.map(job => job.id === jobId ? updatedJob : job));
      toast.success('Job atualizado com sucesso!');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar job');
      console.error('Erro ao atualizar job:', error);
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    if (!confirm('Tem certeza que deseja remover este job?')) return;
    
    try {
      await tituloService.deleteExternalApiJob(jobId);
      setJobs(prev => prev.filter(job => job.id !== jobId));
      toast.success('Job removido com sucesso!');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao remover job');
      console.error('Erro ao remover job:', error);
    }
  };

  const handleExecuteJob = async (jobId: string) => {
    try {
      const result = await tituloService.executeExternalApiJob(jobId);
      toast.success(`Job executado: ${result.message}`);
      loadData(); // Recarregar para atualizar status
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao executar job');
      console.error('Erro ao executar job:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-100 text-green-800">Ativo</Badge>;
      case 'inactive':
        return <Badge variant="secondary">Inativo</Badge>;
      case 'error':
        return <Badge variant="destructive">Erro</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getLastSyncBadge = (status?: string) => {
    if (!status) return <Badge variant="secondary">Nunca</Badge>;
    
    switch (status) {
      case 'success':
        return <Badge variant="default" className="bg-green-100 text-green-800">Sucesso</Badge>;
      case 'error':
        return <Badge variant="destructive">Erro</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2">Carregando configurações...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Configuração da API Externa */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Database className="h-5 w-5 mr-2" />
            Configuração da API Externa
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status da configuração */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
            <div className="flex items-center space-x-3">
              {config.enabled ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              <div>
                <p className="font-medium">
                  {config.enabled ? 'Integração Habilitada' : 'Integração Desabilitada'}
                </p>
                <p className="text-sm text-gray-600 dark:text-slate-400">
                  {config.enabled ? 'API externa configurada e ativa' : 'Configure a API externa para habilitar'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {config.lastSyncAt && (
                <div className="text-right">
                  <p className="text-sm font-medium">Última Sincronização</p>
                  <p className="text-xs text-gray-600 dark:text-slate-400">
                    {format(new Date(config.lastSyncAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                  </p>
                  {getLastSyncBadge(config.lastSyncStatus)}
                </div>
              )}
            </div>
          </div>

          {/* Formulário de configuração */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Habilitar Integração
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={config.enabled}
                    onChange={(e) => setConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm">Ativar sincronização com sistema financeiro</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  URL da API *
                </label>
                <Input
                  type="url"
                  value={config.url}
                  onChange={(e) => setConfig(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://api.sistema-financeiro.com"
                  disabled={!config.enabled}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Token de Autenticação *
                </label>
                <Input
                  type="password"
                  value={config.token}
                  onChange={(e) => setConfig(prev => ({ ...prev, token: e.target.value }))}
                  placeholder="Seu token de acesso"
                  disabled={!config.enabled}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  CNPJ *
                </label>
                <Input
                  value={config.cnpj}
                  onChange={(e) => setConfig(prev => ({ ...prev, cnpj: e.target.value }))}
                  placeholder="12345678000199"
                  disabled={!config.enabled}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Timeout (ms)
                </label>
                <Input
                  type="number"
                  value={config.timeout}
                  onChange={(e) => setConfig(prev => ({ ...prev, timeout: parseInt(e.target.value) || 30000 }))}
                  min="1000"
                  max="300000"
                  disabled={!config.enabled}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Data Inicial *
                </label>
                <Input
                  type="date"
                  value={config.dataInicio || getDefaultPeriod().dataInicio}
                  onChange={(e) => setConfig(prev => ({ ...prev, dataInicio: e.target.value }))}
                  disabled={!config.enabled}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Data inicial para consulta de títulos (padrão: 01/06/2025)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Data Final *
                </label>
                <Input
                  type="date"
                  value={config.dataFim || getDefaultPeriod().dataFim}
                  onChange={(e) => setConfig(prev => ({ ...prev, dataFim: e.target.value }))}
                  disabled={!config.enabled}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Data final para consulta de títulos (padrão: hoje + 60 dias)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Período Padrão (dias)
                </label>
                <Input
                  type="number"
                  value={config.defaultPeriodDays}
                  onChange={(e) => setConfig(prev => ({ ...prev, defaultPeriodDays: parseInt(e.target.value) || 30 }))}
                  min="1"
                  max="365"
                  disabled={!config.enabled}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Sincronização Automática
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={config.autoSyncEnabled}
                    onChange={(e) => setConfig(prev => ({ ...prev, autoSyncEnabled: e.target.checked }))}
                    className="rounded"
                    disabled={!config.enabled}
                  />
                  <span className="text-sm">Habilitar sincronização automática</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Intervalo de Sincronização (Cron)
                </label>
                <Input
                  value={config.syncInterval}
                  onChange={(e) => setConfig(prev => ({ ...prev, syncInterval: e.target.value }))}
                  placeholder="0 8 * * *"
                  disabled={!config.enabled || !config.autoSyncEnabled}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Formato: minuto hora dia mês dia-da-semana (ex: 0 8 * * * = 8h todos os dias)
                </p>
              </div>
            </div>
          </div>

          {/* Botões de ação */}
          <div className="flex items-center space-x-4 pt-4 border-t">
            <Button
              onClick={handleSaveConfig}
              disabled={saving}
              className="flex items-center"
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {saving ? 'Salvando...' : 'Salvar Configuração'}
            </Button>

            <Button
              onClick={handleTestConnection}
              disabled={testing || !config.enabled || !config.url || !config.token || !config.cnpj || !config.dataInicio || !config.dataFim}
              variant="outline"
              className="flex items-center"
            >
              {testing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <TestTube className="h-4 w-4 mr-2" />
              )}
              {testing ? 'Testando...' : 'Testar Conexão'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Jobs de Sincronização */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              Jobs de Sincronização
            </CardTitle>
            <Button
              onClick={() => setShowJobModal(true)}
              size="sm"
              className="flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Job
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-slate-400">
              <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum job de sincronização configurado</p>
              <p className="text-sm">Crie um job para automatizar a sincronização</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <div key={job.id} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-medium">{job.name}</h3>
                      <p className="text-sm text-gray-600 dark:text-slate-400">{job.description}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(job.status)}
                      {job.enabled ? (
                        <Badge variant="default" className="bg-blue-100 text-blue-800">Habilitado</Badge>
                      ) : (
                        <Badge variant="secondary">Desabilitado</Badge>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-slate-400">Expressão Cron</p>
                      <p className="text-sm font-mono">{job.cronExpression}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-slate-400">Última Execução</p>
                      <p className="text-sm">
                        {job.lastRun ? format(new Date(job.lastRun), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'Nunca'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-slate-400">Próxima Execução</p>
                      <p className="text-sm">
                        {job.nextRun ? format(new Date(job.nextRun), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'Não agendada'}
                      </p>
                    </div>
                  </div>

                  {job.lastResult && (
                    <div className="mb-4 p-3 bg-gray-50 dark:bg-slate-800 rounded">
                      <p className="text-sm font-medium mb-2">Último Resultado:</p>
                      <div className="flex items-center space-x-2">
                        {job.lastResult.success ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                        <span className="text-sm">{job.lastResult.message}</span>
                        <span className="text-xs text-gray-500">
                          ({job.lastResult.duration}ms)
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={() => handleExecuteJob(job.id)}
                      size="sm"
                      variant="outline"
                      className="flex items-center"
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Executar
                    </Button>
                    
                    <Button
                      onClick={() => handleUpdateJob(job.id, { enabled: !job.enabled })}
                      size="sm"
                      variant="outline"
                      className="flex items-center"
                    >
                      {job.enabled ? (
                        <Pause className="h-4 w-4 mr-1" />
                      ) : (
                        <Play className="h-4 w-4 mr-1" />
                      )}
                      {job.enabled ? 'Pausar' : 'Ativar'}
                    </Button>

                    <Button
                      onClick={() => {
                        setEditingJob(job);
                        setShowJobModal(true);
                      }}
                      size="sm"
                      variant="outline"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>

                    <Button
                      onClick={() => handleDeleteJob(job.id)}
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Resultado do Teste */}
      {showTestResult && testResult && (
        <Modal
          isOpen={showTestResult}
          onClose={() => setShowTestResult(false)}
          title="Resultado do Teste de Conexão"
        >
          <div className="space-y-4">
            <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-center space-x-2">
                {testResult.success ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
                <p className={`font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {testResult.message}
                </p>
              </div>
            </div>

            {testResult.details && (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-2">Detalhes da Consulta:</h4>
                  <p className="text-sm">
                    <strong>Títulos encontrados:</strong> {testResult.details.total}
                  </p>
                  <p className="text-sm">
                    <strong>Período:</strong> {testResult.details.periodo.inicio} até {testResult.details.periodo.fim}
                  </p>
                </div>

                {testResult.details.statistics && testResult.details.statistics.empresas.length > 0 && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium mb-4">Estatísticas por Empresa:</h4>
                    <div className="space-y-6">
                      {testResult.details.statistics.empresas.map((empresa, index) => (
                        <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                          <h5 className="font-semibold text-base mb-3">{empresa.nome}</h5>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Informações Gerais */}
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">Informações Gerais</p>
                              <p className="text-sm">
                                <strong>Clientes Devedores:</strong> {empresa.totalClientes}
                              </p>
                            </div>

                            {/* Valores por Tipo */}
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">Valores por Tipo</p>
                              <div className="space-y-1">
                                <p className="text-sm">
                                  <strong>Multas:</strong> R$ {empresa.valores.multas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                                <p className="text-sm">
                                  <strong>Locações:</strong> R$ {empresa.valores.locacoes.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                                <p className="text-sm">
                                  <strong>Diversos:</strong> R$ {empresa.valores.diversos.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                              </div>
                            </div>

                            {/* Valores por Status do Contrato */}
                            <div className="md:col-span-2">
                              <p className="text-sm font-medium text-gray-700 mb-2">Valores por Status do Contrato</p>
                              <div className="grid grid-cols-3 gap-2">
                                <div className="p-2 bg-red-50 rounded">
                                  <p className="text-xs font-medium text-red-800">Cancelado</p>
                                  <p className="text-sm font-semibold text-red-900">
                                    R$ {empresa.porStatus.cancelado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </p>
                                </div>
                                <div className="p-2 bg-yellow-50 rounded">
                                  <p className="text-xs font-medium text-yellow-800">Congelado</p>
                                  <p className="text-sm font-semibold text-yellow-900">
                                    R$ {empresa.porStatus.congelado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </p>
                                </div>
                                <div className="p-2 bg-green-50 rounded">
                                  <p className="text-xs font-medium text-green-800">Ativo(a)</p>
                                  <p className="text-sm font-semibold text-green-900">
                                    R$ {empresa.porStatus.ativo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end">
              <Button onClick={() => setShowTestResult(false)}>
                Fechar
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de Job */}
      {showJobModal && (
        <JobModal
          isOpen={showJobModal}
          onClose={() => {
            setShowJobModal(false);
            setEditingJob(null);
          }}
          job={editingJob}
          onSave={editingJob ? 
            (updates) => handleUpdateJob(editingJob.id, updates) :
            handleCreateJob
          }
        />
      )}
    </div>
  );
};

// Componente Modal para Jobs
interface JobModalProps {
  isOpen: boolean;
  onClose: () => void;
  job?: ExternalApiJob | null;
  onSave: (data: any) => void;
}

const JobModal: React.FC<JobModalProps> = ({ isOpen, onClose, job, onSave }) => {
  const [formData, setFormData] = useState({
    name: job?.name || '',
    description: job?.description || '',
    cronExpression: job?.cronExpression || '0 8 * * *',
    enabled: job?.enabled || false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  const cronExamples = [
    { expression: '0 8 * * *', description: 'Todos os dias às 8h' },
    { expression: '0 */6 * * *', description: 'A cada 6 horas' },
    { expression: '0 9 * * 1', description: 'Toda segunda-feira às 9h' },
    { expression: '0 0 1 * *', description: 'Todo dia 1 do mês à meia-noite' },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={job ? 'Editar Job' : 'Novo Job de Sincronização'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Nome do Job *
          </label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Ex: Sincronização Diária"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Descrição
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Descreva o propósito deste job..."
            className="w-full p-2 border rounded-md resize-none"
            rows={3}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Expressão Cron *
          </label>
          <Input
            value={formData.cronExpression}
            onChange={(e) => setFormData(prev => ({ ...prev, cronExpression: e.target.value }))}
            placeholder="0 8 * * *"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Formato: minuto hora dia mês dia-da-semana
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Exemplos de Expressões Cron
          </label>
          <div className="space-y-2">
            {cronExamples.map((example, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="font-mono text-sm">{example.expression}</span>
                <span className="text-sm text-gray-600">{example.description}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setFormData(prev => ({ ...prev, cronExpression: example.expression }))}
                >
                  Usar
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.enabled}
              onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
              className="rounded"
            />
            <span className="text-sm">Habilitar job automaticamente</span>
          </label>
        </div>

        <div className="flex justify-end space-x-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit">
            {job ? 'Atualizar' : 'Criar'} Job
          </Button>
        </div>
      </form>
    </Modal>
  );
};
